import { EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Cache } from 'aws-amplify';
import { FilterOptionsService } from '../../../../../core/filter-options/filter-options.service';
import { SearchableSelectComponent } from '../searchable-select/searchable-select.component';
var HierarchicalFiltersComponent = /** @class */ (function () {
    function HierarchicalFiltersComponent(filterService, cdr) {
        this.filterService = filterService;
        this.cdr = cdr;
        this.role = 'sadmin';
        this.showDistributor = true;
        this.showMarket = true;
        this.showClient = true;
        this.showCampus = true;
        this.showMachine = false;
        this.disabled = false;
        this.multiple = true; // Default to true for backward compatibility
        this.includeAllOption = false;
        this.retailOnly = false;
        // Only clients, campuses, and machines with retail equipment (card readers, cold brew fridges),
        // as the Retail Analytics page counts them. Separate from retailOnly (the machine's mode).
        this.retailEquipmentOnly = false;
        this.filterChange = new EventEmitter();
        // Track which dropdown is currently open
        this.currentlyOpenDropdown = null;
        // Internal state
        this.selectedDistributors = [];
        this.selectedMarkets = [];
        this.selectedClients = [];
        this.selectedCampuses = [];
        this.selectedMachines = [];
        // Observable options
        this.distributorOptions$ = new BehaviorSubject([]);
        this.marketOptions$ = new BehaviorSubject([]);
        this.clientOptions$ = new BehaviorSubject([]);
        this.campusOptions$ = new BehaviorSubject([]);
        this.machineOptions$ = new BehaviorSubject([]);
        // Loading states for each filter type
        this.loadingMarkets = false;
        this.loadingClients = false;
        this.loadingCampuses = false;
        this.loadingMachines = false;
        // Base scope: role-based IDs that should always be preserved when clearing
        this.baseScopeDistributorIds = [];
        this.baseScopeMarketIds = [];
        this.baseScopeClientIds = [];
        // Display value functions for each filter type
        this.getDistributorDisplay = function (selected) {
            if (selected.length === 0)
                return 'All Distributors';
            if (selected.length <= 3)
                return selected.map(function (s) { return s.label; }).join(', ');
            return selected.length + " selected";
        };
        this.getMarketDisplay = function (selected) {
            if (selected.length === 0)
                return 'All Markets';
            if (selected.length <= 3)
                return selected.map(function (s) { return s.label; }).join(', ');
            return selected.length + " selected";
        };
        this.getClientDisplay = function (selected) {
            if (selected.length === 0)
                return 'All Clients';
            if (selected.length <= 3)
                return selected.map(function (s) { return s.label; }).join(', ');
            return selected.length + " selected";
        };
        this.getCampusDisplay = function (selected) {
            if (selected.length === 0)
                return 'All Campuses';
            if (selected.length <= 3)
                return selected.map(function (s) { return s.label; }).join(', ');
            return selected.length + " selected";
        };
        this.getMachineDisplay = function (selected) {
            if (selected.length === 0)
                return 'All Machines';
            if (selected.length <= 3)
                return selected.map(function (s) { return s.label; }).join(', ');
            return selected.length + " selected";
        };
    }
    HierarchicalFiltersComponent.prototype.ngOnInit = function () {
        this.user = Cache.getItem('userAttr');
        this.initializeFilters();
        this.loadInitialOptions();
    };
    HierarchicalFiltersComponent.prototype.ngOnChanges = function (changes) {
        if (changes['initialValues'] && !changes['initialValues'].firstChange) {
            this.initializeFilters();
            this.loadInitialOptions();
        }
        if (changes['role'] && !changes['role'].firstChange) {
            // Re-determine base scope when role changes
            this.determineBaseScope();
            this.loadInitialOptions();
        }
    };
    Object.defineProperty(HierarchicalFiltersComponent.prototype, "hasDistributor", {
        get: function () {
            return this.selectedDistributors.length > 0 && this.selectedDistributors[0] !== null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HierarchicalFiltersComponent.prototype, "hasClient", {
        get: function () {
            return this.selectedClients.length > 0 && this.selectedClients[0] !== null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HierarchicalFiltersComponent.prototype, "hasCampus", {
        get: function () {
            return this.selectedCampuses.length > 0 && this.selectedCampuses[0] !== null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HierarchicalFiltersComponent.prototype, "hasAnySelection", {
        get: function () {
            return this.hasDistributor || this.selectedMarkets.length > 0 || this.hasClient ||
                this.selectedCampuses.length > 0 || this.selectedMachines.length > 0;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(HierarchicalFiltersComponent.prototype, "hasVisibleFilters", {
        get: function () {
            return this.showDistributor || this.showMarket || this.showClient ||
                this.showCampus || this.showMachine;
        },
        enumerable: true,
        configurable: true
    });
    HierarchicalFiltersComponent.prototype.initializeFilters = function () {
        // Determine base scope (role-based IDs that should be preserved)
        this.determineBaseScope();
        if (this.initialValues) {
            this.selectedDistributors = this.initialValues.distributorIds || [];
            this.selectedMarkets = this.initialValues.marketIds || [];
            this.selectedClients = this.initialValues.clientIds || [];
            this.selectedCampuses = this.initialValues.campusIds || [];
            this.selectedMachines = this.initialValues.machineIds || [];
        }
        else {
            // Initialize from base scope
            this.selectedDistributors = this.baseScopeDistributorIds.slice();
            this.selectedMarkets = this.baseScopeMarketIds.slice();
            this.selectedClients = this.baseScopeClientIds.slice();
            // Campus ID is not preserved in base scope (it's a more specific filter)
            if (this.user && this.user['custom:campus_id']) {
                this.selectedCampuses = [this.user['custom:campus_id']];
            }
        }
    };
    /**
     * Determines the base scope (role-based IDs) that should always be preserved.
     * This is the minimum scope a user should have based on their role.
     */
    HierarchicalFiltersComponent.prototype.determineBaseScope = function () {
        this.baseScopeDistributorIds = [];
        this.baseScopeMarketIds = [];
        this.baseScopeClientIds = [];
        if (!this.user) {
            return;
        }
        // For distiadmin: preserve distributor ID
        if (this.role === 'distiadmin' && this.user['custom:disti_id']) {
            this.baseScopeDistributorIds = [this.user['custom:disti_id']];
        }
        // For marketadmin: preserve market ID
        if (this.role === 'marketadmin' && this.user['custom:market_id']) {
            this.baseScopeMarketIds = [this.user['custom:market_id']];
        }
        // For marketadmin: also preserve distributor ID for create flows
        if (this.role === 'marketadmin' && this.user['custom:disti_id']) {
            this.baseScopeDistributorIds = [this.user['custom:disti_id']];
        }
        // For clientadmin: preserve client ID
        if (this.role === 'clientadmin' && this.user['custom:client_id']) {
            this.baseScopeClientIds = [this.user['custom:client_id']];
        }
    };
    HierarchicalFiltersComponent.prototype.loadInitialOptions = function () {
        // Load distributors if role allows
        if (this.showDistributor && this.role === 'sadmin') {
            this.loadDistributors();
        }
        // For distiadmin, auto-load markets and clients if distributor is set
        if (this.role === 'distiadmin' && this.user && this.user['custom:disti_id']) {
            if (!this.hasDistributor) {
                this.selectedDistributors = [this.user['custom:disti_id']];
            }
            if (this.showMarket) {
                this.loadMarkets();
            }
            if (this.showClient) {
                this.loadClients();
            }
        }
        // For marketadmin, auto-load clients and campuses if market is set
        if (this.role === 'marketadmin' && this.user && this.user['custom:market_id']) {
            if (this.selectedMarkets.length === 0) {
                this.selectedMarkets = [this.user['custom:market_id']];
            }
            if (this.showClient) {
                this.loadClients();
            }
            if (this.showCampus && this.user['custom:client_id']) {
                if (!this.hasClient) {
                    this.selectedClients = [this.user['custom:client_id']];
                }
                this.loadCampuses();
            }
        }
        // For clientadmin, auto-load campuses if client is set
        if (this.role === 'clientadmin' && this.user && this.user['custom:client_id']) {
            if (!this.hasClient) {
                this.selectedClients = [this.user['custom:client_id']];
            }
            if (this.showCampus) {
                this.loadCampuses();
            }
        }
        // Load markets only if distributor is selected and showMarket is true
        if (this.showMarket && this.hasDistributor) {
            this.loadMarkets();
        }
        // Load clients, campuses, and machines (they work independently)
        // Skip loading clients if showClient is false or role is clientadmin (they already have their client ID)
        // For marketadmin, clients are loaded above if market is set
        if (this.showClient && this.role !== 'clientadmin' && this.role !== 'marketadmin') {
            this.loadClients();
        }
        // Skip loading campuses if showCampus is false or role is campusadmin (they already have their campus ID)
        if (this.showCampus && this.role !== 'campusadmin') {
            this.loadCampuses();
        }
        if (this.showMachine) {
            this.loadMachines();
        }
        this.emitFilterChange();
    };
    HierarchicalFiltersComponent.prototype.loadDistributors = function () {
        var _this = this;
        this.filterService.loadDistributors(this.role, this.includeAllOption)
            .subscribe(function (options) {
            _this.distributorOptions$.next(options);
            _this.cdr.detectChanges();
        });
    };
    HierarchicalFiltersComponent.prototype.loadMarkets = function () {
        var _this = this;
        this.loadingMarkets = true;
        this.marketOptions$.next([]); // Clear old options immediately
        var distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
        this.filterService.loadMarkets(distributorIds, this.includeAllOption)
            .subscribe({
            next: function (options) {
                _this.marketOptions$.next(options);
                _this.loadingMarkets = false;
                _this.cdr.detectChanges();
            },
            error: function () {
                _this.loadingMarkets = false;
                _this.cdr.detectChanges();
            }
        });
    };
    HierarchicalFiltersComponent.prototype.loadClients = function () {
        var _this = this;
        this.loadingClients = true;
        this.clientOptions$.next([]); // Clear old options immediately
        // For marketadmin, always use their market scope
        var distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
        var marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;
        // If marketadmin and no markets selected, use base scope market
        if (this.role === 'marketadmin' && (!marketIds || marketIds.length === 0) && this.baseScopeMarketIds.length > 0) {
            marketIds = this.baseScopeMarketIds;
        }
        this.filterService.loadClients(distributorIds, marketIds, this.includeAllOption, this.retailOnly, this.retailEquipmentOnly)
            .subscribe({
            next: function (options) {
                _this.clientOptions$.next(options);
                _this.loadingClients = false;
                _this.cdr.detectChanges();
            },
            error: function () {
                _this.loadingClients = false;
                _this.cdr.detectChanges();
            }
        });
    };
    HierarchicalFiltersComponent.prototype.loadCampuses = function () {
        var _this = this;
        this.loadingCampuses = true;
        this.campusOptions$.next([]); // Clear old options immediately
        var clientIds = this.selectedClients.length > 0 ? this.selectedClients : null;
        var distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
        var marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;
        this.filterService.loadCampuses(clientIds, distributorIds, marketIds, this.includeAllOption, this.retailOnly, this.retailEquipmentOnly)
            .subscribe({
            next: function (options) {
                _this.campusOptions$.next(options);
                _this.loadingCampuses = false;
                _this.cdr.detectChanges();
            },
            error: function () {
                _this.loadingCampuses = false;
                _this.cdr.detectChanges();
            }
        });
    };
    HierarchicalFiltersComponent.prototype.loadMachines = function () {
        var _this = this;
        this.loadingMachines = true;
        this.machineOptions$.next([]); // Clear old options immediately
        var campusIds = this.selectedCampuses.length > 0 ? this.selectedCampuses : null;
        var clientIds = this.selectedClients.length > 0 ? this.selectedClients : null;
        var distributorIds = this.selectedDistributors.length > 0 ? this.selectedDistributors : null;
        var marketIds = this.selectedMarkets.length > 0 ? this.selectedMarkets : null;
        this.filterService.loadMachines(campusIds, clientIds, distributorIds, marketIds, this.includeAllOption, this.retailEquipmentOnly)
            .subscribe({
            next: function (options) {
                _this.machineOptions$.next(options);
                _this.loadingMachines = false;
                _this.cdr.detectChanges();
            },
            error: function () {
                _this.loadingMachines = false;
                _this.cdr.detectChanges();
            }
        });
    };
    HierarchicalFiltersComponent.prototype.onDistributorChange = function (values) {
        // Normalize input: convert single value to array, handle null
        var normalizedValues = this.normalizeToArray(values);
        this.selectedDistributors = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
        // Reset dependent filters
        this.selectedMarkets = [];
        this.selectedClients = [];
        this.selectedCampuses = [];
        this.selectedMachines = [];
        // Reload markets only if distributor is selected
        if (this.selectedDistributors.length > 0 && this.selectedDistributors[0] !== null) {
            this.loadMarkets();
        }
        else {
            this.marketOptions$.next([]);
        }
        // Always reload clients, campuses, and machines (they work independently)
        this.loadClients();
        this.loadCampuses();
        this.loadMachines();
        this.emitFilterChange();
    };
    HierarchicalFiltersComponent.prototype.onMarketChange = function (values) {
        // Normalize input: convert single value to array, handle null
        var normalizedValues = this.normalizeToArray(values);
        this.selectedMarkets = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
        // Reset dependent filters
        this.selectedClients = [];
        this.selectedCampuses = [];
        this.selectedMachines = [];
        // Always reload clients, campuses, and machines (they work independently)
        this.loadClients();
        this.loadCampuses();
        this.loadMachines();
        this.emitFilterChange();
    };
    HierarchicalFiltersComponent.prototype.onClientChange = function (values) {
        // Normalize input: convert single value to array, handle null
        var normalizedValues = this.normalizeToArray(values);
        this.selectedClients = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
        // Reset dependent filters
        this.selectedCampuses = [];
        this.selectedMachines = [];
        // Always reload campuses and machines (they work independently)
        this.loadCampuses();
        this.loadMachines();
        this.emitFilterChange();
    };
    HierarchicalFiltersComponent.prototype.onCampusChange = function (values) {
        // Normalize input: convert single value to array, handle null
        var normalizedValues = this.normalizeToArray(values);
        this.selectedCampuses = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
        // Reset dependent filters
        this.selectedMachines = [];
        // Always reload machines (they work independently)
        this.loadMachines();
        this.emitFilterChange();
    };
    HierarchicalFiltersComponent.prototype.onMachineChange = function (values) {
        // Normalize input: convert single value to array, handle null
        var normalizedValues = this.normalizeToArray(values);
        this.selectedMachines = this.multiple ? normalizedValues : (normalizedValues.length > 0 ? [normalizedValues[0]] : []);
        this.emitFilterChange();
    };
    /**
     * Normalize a value that could be a single value, array, or null into an array
     */
    HierarchicalFiltersComponent.prototype.normalizeToArray = function (value) {
        if (value === null || value === undefined) {
            return [];
        }
        if (Array.isArray(value)) {
            return value;
        }
        return [value];
    };
    HierarchicalFiltersComponent.prototype.emitFilterChange = function () {
        var params = {
            disti_id: this.multiple
                ? (this.selectedDistributors.length > 0 ? this.selectedDistributors : null)
                : (this.selectedDistributors.length > 0 ? (this.selectedDistributors[0] || null) : null),
            market_id: this.multiple
                ? (this.selectedMarkets.length > 0 ? this.selectedMarkets : null)
                : (this.selectedMarkets.length > 0 ? (this.selectedMarkets[0] || null) : null),
            client_id: this.multiple
                ? (this.selectedClients.length > 0 ? this.selectedClients : null)
                : (this.selectedClients.length > 0 ? (this.selectedClients[0] || null) : null),
            campus_id: this.multiple
                ? (this.selectedCampuses.length > 0 ? this.selectedCampuses : null)
                : (this.selectedCampuses.length > 0 ? (this.selectedCampuses[0] || null) : null),
            machine_id: this.multiple
                ? (this.selectedMachines.length > 0 ? this.selectedMachines : null)
                : (this.selectedMachines.length > 0 ? (this.selectedMachines[0] || null) : null)
        };
        this.filterChange.emit(params);
    };
    // Public method to get current filter params
    HierarchicalFiltersComponent.prototype.getFilterParams = function () {
        return {
            disti_id: this.multiple
                ? (this.selectedDistributors.length > 0 ? this.selectedDistributors : null)
                : (this.selectedDistributors.length > 0 ? (this.selectedDistributors[0] || null) : null),
            market_id: this.multiple
                ? (this.selectedMarkets.length > 0 ? this.selectedMarkets : null)
                : (this.selectedMarkets.length > 0 ? (this.selectedMarkets[0] || null) : null),
            client_id: this.multiple
                ? (this.selectedClients.length > 0 ? this.selectedClients : null)
                : (this.selectedClients.length > 0 ? (this.selectedClients[0] || null) : null),
            campus_id: this.multiple
                ? (this.selectedCampuses.length > 0 ? this.selectedCampuses : null)
                : (this.selectedCampuses.length > 0 ? (this.selectedCampuses[0] || null) : null),
            machine_id: this.multiple
                ? (this.selectedMachines.length > 0 ? this.selectedMachines : null)
                : (this.selectedMachines.length > 0 ? (this.selectedMachines[0] || null) : null)
        };
    };
    HierarchicalFiltersComponent.prototype.clearScope = function () {
        // Reset to base scope (role-based IDs that should always be preserved)
        this.selectedDistributors = this.baseScopeDistributorIds.slice();
        this.selectedMarkets = this.baseScopeMarketIds.slice();
        this.selectedClients = this.baseScopeClientIds.slice();
        this.selectedCampuses = [];
        this.selectedMachines = [];
        // Reset dependent options
        this.marketOptions$.next([]);
        this.clientOptions$.next([]);
        this.campusOptions$.next([]);
        this.machineOptions$.next([]);
        // Reload options based on base scope
        if (this.selectedDistributors.length > 0) {
            this.loadMarkets();
        }
        // For marketadmin, load clients using their market scope
        if (this.role === 'marketadmin' && this.baseScopeMarketIds.length > 0) {
            this.loadClients();
        }
        else {
            // Always reload clients, campuses, and machines (they work independently and can handle null params)
            this.loadClients();
        }
        this.loadCampuses();
        this.loadMachines();
        this.emitFilterChange();
    };
    /**
     * Handle dropdown open event - close any other open dropdowns
     */
    HierarchicalFiltersComponent.prototype.onDropdownOpen = function (openedDropdown) {
        // If there's another dropdown open, close it and submit its changes
        if (this.currentlyOpenDropdown && this.currentlyOpenDropdown !== openedDropdown) {
            this.currentlyOpenDropdown.closeDropdownProgrammatically();
            this.currentlyOpenDropdown = null; // Clear tracking immediately
        }
        // Track the newly opened dropdown
        this.currentlyOpenDropdown = openedDropdown;
    };
    /**
     * Handle dropdown close event - clear tracking
     */
    HierarchicalFiltersComponent.prototype.onDropdownClose = function () {
        // Clear tracking when dropdown closes
        this.currentlyOpenDropdown = null;
    };
    /**
     * Close all dropdowns programmatically
     */
    HierarchicalFiltersComponent.prototype.closeAllDropdowns = function () {
        var dropdowns = [
            this.distributorSelect,
            this.marketSelect,
            this.clientSelect,
            this.campusSelect,
            this.machineSelect
        ].filter(function (d) { return d !== undefined; });
        dropdowns.forEach(function (dropdown) {
            if (dropdown) {
                dropdown.closeDropdownProgrammatically();
            }
        });
        this.currentlyOpenDropdown = null;
    };
    return HierarchicalFiltersComponent;
}());
export { HierarchicalFiltersComponent };
